import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useToast } from '../../contexts/ToastContext';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { displayDateTimeToUTC, utcToDisplayDateAndTime, getTimezoneLabel } from '../../utils/timezoneUtils';
import UpcomingMaintenanceItemSummary from './UpcomingMaintenanceItemSummary';

dayjs.extend(utc);
dayjs.extend(timezone);

const CreateStandaloneIntimationDialog = ({ open, onClose, aircraft = [], onSuccess, initialPrefill, displayTimezone }) => {
  const [aircraftId, setAircraftId] = useState('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState('MAINTENANCE');
  const [includedItems, setIncludedItems] = useState('');
  const [excludedItems, setExcludedItems] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledDate, setScheduledDate] = useState(null);
  const [sourcePlainText, setSourcePlainText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isAdHoc = !initialPrefill || Object.keys(initialPrefill).length === 0;

  const { showToast } = useToast();

  useEffect(() => {
    if (open) {
      if (initialPrefill && Object.keys(initialPrefill).length > 0) {
        setAircraftId(initialPrefill.aircraftId || '');
        setTitle(initialPrefill.title || '');
        setType(initialPrefill.type || 'MAINTENANCE');
        setIncludedItems(initialPrefill.includedItems || '');
        setExcludedItems(initialPrefill.excludedItems || '');
        setDescription(initialPrefill.description || '');
        if (initialPrefill.scheduledDate) {
          const iso = typeof initialPrefill.scheduledDate === 'string' ? initialPrefill.scheduledDate : initialPrefill.scheduledDate?.toISOString?.();
          if (displayTimezone && iso) {
            const { date: d, time: t } = utcToDisplayDateAndTime(iso, displayTimezone);
            setScheduledDate(d && t ? dayjs.tz(`${d}T${t}`, displayTimezone) : (typeof initialPrefill.scheduledDate === 'string' ? new Date(initialPrefill.scheduledDate) : initialPrefill.scheduledDate));
          } else {
            setScheduledDate(typeof initialPrefill.scheduledDate === 'string' ? new Date(initialPrefill.scheduledDate) : initialPrefill.scheduledDate);
          }
        } else {
          setScheduledDate(null);
        }
        setSourcePlainText('');
      } else {
        setAircraftId('');
        setTitle('');
        setType('MAINTENANCE');
        setIncludedItems('');
        setExcludedItems('');
        setDescription('');
        setScheduledDate(null);
        setSourcePlainText('');
      }
      setError('');
    }
  }, [open, initialPrefill, displayTimezone]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (!aircraftId) {
        setError('Aircraft is required');
        return;
      }
      if (!title.trim()) {
        setError('Title is required');
        return;
      }
      if (!includedItems.trim()) {
        setError('Included items are required');
        return;
      }
      if (isAdHoc && !(sourcePlainText || '').trim()) {
        setError('Source (plain text) is required when creating an intimation from this tab.');
        return;
      }

      let scheduledDateISO;
      if (scheduledDate) {
        if (displayTimezone && dayjs.isDayjs(scheduledDate)) {
          scheduledDateISO = displayDateTimeToUTC(scheduledDate.format('YYYY-MM-DD'), scheduledDate.format('HH:mm'), displayTimezone) ?? scheduledDate.toISOString();
        } else {
          scheduledDateISO = scheduledDate.toISOString?.() ?? dayjs(scheduledDate).toISOString();
        }
      } else {
        scheduledDateISO = undefined;
      }
      const payload = {
        aircraftId,
        title: title.trim(),
        type,
        includedItems: includedItems.trim(),
        excludedItems: excludedItems.trim() || undefined,
        description: description.trim() || undefined,
        intimationDescription: description.trim() || undefined,
        scheduledDate: scheduledDateISO
      };
      if (initialPrefill?.sourceType) payload.sourceType = initialPrefill.sourceType;
      if (initialPrefill?.sourceId != null && initialPrefill.sourceId !== '') payload.sourceId = String(initialPrefill.sourceId);
      if (initialPrefill?.sourceMetadata && typeof initialPrefill.sourceMetadata === 'object') payload.sourceMetadata = initialPrefill.sourceMetadata;
      if ((sourcePlainText || '').trim()) payload.sourcePlainText = (sourcePlainText || '').trim();
      await axios.post('/api/intimations', payload);

      showToast('Intimation created successfully. CA Manager/CA Technical Officer will be notified.', 'success');
      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Failed to create intimation';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Intimation to Create Work Order</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Create a request for CA Manager and CA Technical Officer to create a work order. They will be notified and can create the work order and acknowledge, or request clarification.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {initialPrefill?.upcomingItem && (
          <UpcomingMaintenanceItemSummary
            item={initialPrefill.upcomingItem}
            displayTimezone={displayTimezone}
            title="Source item from Upcoming Maintenance"
          />
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Aircraft</InputLabel>
              <Select
                value={aircraftId}
                onChange={(e) => setAircraftId(e.target.value)}
                label="Aircraft"
              >
                {aircraft.map((ac) => (
                  <MenuItem key={ac._id} value={ac._id}>
                    {ac.registration} {ac.manufacturer ? `– ${ac.manufacturer}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value)}
                label="Type"
              >
                <MenuItem value="INSPECTION">Inspection</MenuItem>
                <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                <MenuItem value="REPAIR">Defect</MenuItem>
                <MenuItem value="SCHEDULED">Scheduled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title *"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. C-Check Phase 1"
              required
            />
          </Grid>
          {isAdHoc && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Source (plain text) *"
                value={sourcePlainText}
                onChange={(e) => setSourcePlainText(e.target.value)}
                placeholder="e.g. Manual request, Phone call from Ops, Email from..."
                required
                helperText="Describe the source of this intimation when not created from an Upcoming Maintenance item."
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Included Items *"
              value={includedItems}
              onChange={(e) => setIncludedItems(e.target.value)}
              placeholder="What should be included in the work order..."
              required
              helperText="Specify all items, tasks, or requirements that should be included"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Excluded Items"
              value={excludedItems}
              onChange={(e) => setExcludedItems(e.target.value)}
              placeholder="What should be excluded (optional)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Additional Description / Remarks"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Additional notes..."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label={displayTimezone ? `Preferred scheduled date (optional) (${getTimezoneLabel(displayTimezone)})` : 'Preferred scheduled date (optional)'}
              value={scheduledDate ? (dayjs.isDayjs(scheduledDate) ? scheduledDate : dayjs(scheduledDate)) : null}
              onChange={(v) => setScheduledDate(v ? (displayTimezone ? dayjs.tz(v.format('YYYY-MM-DD') + 'T' + v.format('HH:mm'), displayTimezone) : v) : null)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !aircraftId || !title.trim() || !includedItems.trim() || (isAdHoc && !(sourcePlainText || '').trim())}
        >
          {loading ? 'Creating...' : 'Create Intimation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateStandaloneIntimationDialog;
